import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ExternalLink } from '../../auth/types';
import { client } from '../generated/client.gen';
import { refreshMe } from './me';

export type TenantStatus = 'active' | 'archived';
export type AdminMemberStatus = 'invited' | 'active' | 'disabled';

export interface AdminTenant {
  id: string;
  name: string;
  slug: string;
  status: TenantStatus | string;
  accentColor?: string;
  logoInitials?: string;
  externalLinks?: ExternalLink[];
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

export interface AdminTenantStats {
  contacts: number;
  activeMembers: number;
  pendingInvites: number;
  tags: number;
  segments: number;
  consents: number;
  auditEvents30d: number;
  lastActivityAt?: string | null;
}

export interface AdminMember {
  id: string;
  tenantId: string;
  userId?: string;
  invitedEmail?: string;
  status: AdminMemberStatus | string;
  invitedBy?: string;
  invitedAt: string;
  joinedAt?: string;
}

export interface AdminTenantView {
  id: string;
  name: string;
  color?: string;
  filters: Record<string, unknown>;
  scope: string;
}

const tenantsKey = ['admin', 'tenants'] as const;
const tenantKey = (id: string) => ['admin', 'tenants', id] as const;
const tenantStatsKey = (id: string) => ['admin', 'tenants', id, 'stats'] as const;
const tenantMembersKey = (id: string) => ['admin', 'tenants', id, 'members'] as const;
const tenantViewsKey = (id: string) => ['admin', 'tenants', id, 'views'] as const;

function useTenants() {
  return useQuery({
    queryKey: tenantsKey,
    queryFn: async (): Promise<AdminTenant[]> => {
      const { data } = await client.instance.get<{ data: AdminTenant[] }>('/admin/tenants');
      return data.data;
    },
  });
}

function useTenant(id: string) {
  return useQuery({
    queryKey: tenantKey(id),
    queryFn: async (): Promise<AdminTenant> => {
      const { data } = await client.instance.get<AdminTenant>(`/admin/tenants/${id}`);
      return data;
    },
    enabled: !!id,
  });
}

function useTenantStats(id: string) {
  return useQuery({
    queryKey: tenantStatsKey(id),
    queryFn: async (): Promise<AdminTenantStats> => {
      const { data } = await client.instance.get<AdminTenantStats>(`/admin/tenants/${id}/stats`);
      return data;
    },
    enabled: !!id,
  });
}

function useTenantMembers(tenantId: string) {
  return useQuery({
    queryKey: tenantMembersKey(tenantId),
    queryFn: async (): Promise<AdminMember[]> => {
      const { data } = await client.instance.get<{ data: AdminMember[] }>(
        `/admin/tenants/${tenantId}/members`,
      );
      return data.data;
    },
    enabled: !!tenantId,
  });
}

function useCreateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { name: string; slug?: string }): Promise<AdminTenant> => {
      const { data } = await client.instance.post<AdminTenant>('/admin/tenants', body);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tenantsKey });
    },
  });
}

function useRenameTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }): Promise<AdminTenant> => {
      const { data } = await client.instance.patch<AdminTenant>(`/admin/tenants/${id}`, { name });
      return data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: tenantsKey });
      qc.invalidateQueries({ queryKey: tenantKey(vars.id) });
    },
  });
}

function useUpdateTenantConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      accentColor,
      logoInitials,
    }: {
      id: string;
      accentColor?: string;
      logoInitials?: string;
    }): Promise<AdminTenant> => {
      const { data } = await client.instance.patch<AdminTenant>(`/admin/tenants/${id}`, {
        accentColor,
        logoInitials,
      });
      return data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: tenantsKey });
      qc.invalidateQueries({ queryKey: tenantKey(vars.id) });
    },
  });
}

function useUpdateTenantExternalLinks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      externalLinks,
    }: {
      id: string;
      externalLinks: ExternalLink[];
    }): Promise<AdminTenant> => {
      const { data } = await client.instance.patch<AdminTenant>(`/admin/tenants/${id}`, {
        externalLinks,
      });
      return data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: tenantsKey });
      qc.invalidateQueries({ queryKey: tenantKey(vars.id) });
      void refreshMe().catch(() => undefined);
    },
  });
}

function useArchiveTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<AdminTenant> => {
      const { data } = await client.instance.patch<AdminTenant>(`/admin/tenants/${id}`, {
        status: 'archived',
      });
      return data;
    },
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: tenantsKey });
      qc.invalidateQueries({ queryKey: tenantKey(id) });
    },
  });
}

// Permanently delete an archived tenant (super-admin only). The API rejects the
// request with 409 unless the tenant is already archived.
function useDeleteTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await client.instance.post(`/admin/tenants/${id}/purge`);
    },
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: tenantsKey });
      qc.removeQueries({ queryKey: tenantKey(id) });
    },
  });
}

function useInviteMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      tenantId,
      email,
    }: {
      tenantId: string;
      email: string;
    }): Promise<AdminMember> => {
      const { data } = await client.instance.post<AdminMember>(
        `/admin/tenants/${tenantId}/members`,
        { email },
      );
      return data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: tenantMembersKey(vars.tenantId) });
      qc.invalidateQueries({ queryKey: tenantStatsKey(vars.tenantId) });
    },
  });
}

function useDeactivateMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      tenantId,
      membershipId,
    }: {
      tenantId: string;
      membershipId: string;
    }): Promise<AdminMember> => {
      const { data } = await client.instance.patch<AdminMember>(
        `/admin/tenants/${tenantId}/members/${membershipId}`,
        { status: 'disabled' },
      );
      return data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: tenantMembersKey(vars.tenantId) });
      qc.invalidateQueries({ queryKey: tenantStatsKey(vars.tenantId) });
    },
  });
}

function useRemoveMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      tenantId,
      membershipId,
    }: {
      tenantId: string;
      membershipId: string;
    }): Promise<void> => {
      await client.instance.delete(`/admin/tenants/${tenantId}/members/${membershipId}`);
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: tenantMembersKey(vars.tenantId) });
      qc.invalidateQueries({ queryKey: tenantStatsKey(vars.tenantId) });
    },
  });
}

function useTenantViews(tenantId: string) {
  return useQuery({
    queryKey: tenantViewsKey(tenantId),
    queryFn: async (): Promise<AdminTenantView[]> => {
      const { data } = await client.instance.get<{ data: AdminTenantView[] }>(
        `/admin/tenants/${tenantId}/views`,
      );
      return data.data;
    },
    enabled: !!tenantId,
  });
}

function useCreateTenantView() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      tenantId,
      name,
      color,
      filters,
    }: {
      tenantId: string;
      name: string;
      color?: string;
      filters: Record<string, unknown>;
    }): Promise<AdminTenantView> => {
      const { data } = await client.instance.post<AdminTenantView>(
        `/admin/tenants/${tenantId}/views`,
        { name, color, filters, scope: 'workspace' },
      );
      return data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: tenantViewsKey(vars.tenantId) });
    },
  });
}

function useDeleteTenantView() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ tenantId, id }: { tenantId: string; id: string }): Promise<void> => {
      await client.instance.delete(`/admin/tenants/${tenantId}/views/${id}`);
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: tenantViewsKey(vars.tenantId) });
    },
  });
}

export const adminApi = {
  useTenants,
  useTenant,
  useTenantStats,
  useTenantMembers,
  useCreateTenant,
  useRenameTenant,
  useUpdateTenantConfig,
  useUpdateTenantExternalLinks,
  useArchiveTenant,
  useDeleteTenant,
  useInviteMember,
  useDeactivateMember,
  useRemoveMember,
  useTenantViews,
  useCreateTenantView,
  useDeleteTenantView,
};
